import type {
  ApiError as ApiErrorBody,
  AuditLogEntry,
  AuthSession,
  BookingAvailability,
  BookingSettings,
  Category,
  CategoryNode,
  ClaimedPrintJob,
  GiveawayReport,
  GuestSource,
  GuestStatus,
  InvoiceSummary,
  InvoicingSettings,
  MenuItem,
  ModifierGroup,
  ModifierOption,
  Order,
  OrderExportRow,
  OrderSummary,
  Paginated,
  PrintJob,
  PrintJobKind,
  PrintJobStatus,
  ProductionTicket,
  Receipt,
  Reservation,
  ReservationStatus,
  RestaurantSettings,
  RestaurantTable,
  SalesReport,
  SplitPaymentQuote,
  StaffUser,
  TicketedDestination,
} from '@sebastian/shared';

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://127.0.0.1:8787';
const TOKEN_KEY = 'eltoro.token';

/** Error carrying the API's machine-readable code so the UI can react to it. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

/** Fired when the API rejects our token so the app can bounce to /login. */
export const AUTH_EXPIRED_EVENT = 'eltoro:auth-expired';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = tokenStore.get();
  const headers = new Headers(init.headers);
  if (init.body !== undefined) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}${path}`, { ...init, headers });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the server. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (body ?? {}) as ApiErrorBody;
    if (response.status === 401) {
      tokenStore.clear();
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }
    throw new ApiError(
      response.status,
      error.code ?? 'UNKNOWN',
      error.error ?? 'Something went wrong',
      error.details,
    );
  }

  return body as T;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });

const query = (params: Record<string, string | number | undefined | null>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
};

export interface SyncSnapshot {
  serverTime: string;
  markers: {
    tables: string | null;
    orders: string | null;
    orderItems: string | null;
    tickets: string | null;
    printJobs: string | null;
  };
  counts: { activeTickets: number; openOrders: number; queuedPrintJobs: number };
}

export interface OrderHistoryResponse extends Paginated<OrderSummary> {
  summary: {
    orderCount: number;
    guestCount: number;
    grossCents: number;
    taxCents: number;
    serviceChargeCents: number;
    /** Orders closed on the house — counted, but kept out of the takings above. */
    invitedCount: number;
    invitedCents: number;
    /** Orders removed before they were settled — counted here only, left out of every figure above. */
    removedCount: number;
    /** Tips on these orders — outside the bill, so never part of gross. */
    tipCents: number;
  };
}

export interface MoveItemsResponse {
  sourceOrder: Order;
  destinationOrderId: string;
  destinationTableNumber: number;
  movedItemCount: number;
}

export interface SendItemsResponse {
  sentAt: string;
  ticketIds: string[];
  sentItemIds: string[];
  untickedItemIds: string[];
  order: Order;
}

export const api = {
  health: () => get<{ status: string }>('/api/health'),
  sync: () => get<SyncSnapshot>('/api/sync'),

  auth: {
    login: (email: string, password: string) =>
      post<AuthSession>('/api/auth/login', { email, password }),
    me: () => get<StaffUser>('/api/auth/me'),
    logout: () => post<{ ok: true }>('/api/auth/logout'),
    changePassword: (currentPassword: string, newPassword: string) =>
      post<{ ok: true }>('/api/auth/change-password', { currentPassword, newPassword }),
  },

  tables: {
    list: (includeInactive = false) =>
      get<RestaurantTable[]>(`/api/tables${query({ includeInactive: includeInactive ? 'true' : '' })}`),
    get: (id: string) => get<RestaurantTable>(`/api/tables/${id}`),
    create: (body: unknown) => post<RestaurantTable>('/api/tables', body),
    update: (id: string, body: unknown) => patch<RestaurantTable>(`/api/tables/${id}`, body),
    remove: (id: string) => del<{ deactivated: boolean }>(`/api/tables/${id}`),
  },

  orders: {
    history: (params: Record<string, string | number | undefined>) =>
      get<OrderHistoryResponse>(`/api/orders${query(params)}`),
    /** Every order matching the history filters, with item lines, unpaginated. */
    exportHistory: (params: Record<string, string | number | undefined>) =>
      get<OrderExportRow[]>(`/api/orders/export${query(params)}`),
    get: (id: string) => get<Order>(`/api/orders/${id}`),
    activeForTable: (tableId: string) => get<Order | null>(`/api/orders/active/${tableId}`),
    receipt: (id: string) => get<Receipt>(`/api/orders/${id}/receipt`),
    open: (
      tableId: string,
      guestCount: number,
      notes?: string | null,
      guest: { guestSource?: GuestSource | null; guestStatus?: GuestStatus | null } = {},
    ) => post<Order>('/api/orders', { tableId, guestCount, notes, ...guest }),
    update: (id: string, body: unknown) => patch<Order>(`/api/orders/${id}`, body),
    addItems: (id: string, items: unknown[]) => post<Order>(`/api/orders/${id}/items`, { items }),
    updateItem: (id: string, itemId: string, body: unknown) =>
      patch<Order>(`/api/orders/${id}/items/${itemId}`, body),
    removeItem: (id: string, itemId: string) => del<Order>(`/api/orders/${id}/items/${itemId}`),
    cancelItem: (id: string, itemId: string, reason: string) =>
      post<Order>(`/api/orders/${id}/items/${itemId}/cancel`, { reason }),
    reorderItems: (id: string, course: number, itemIds: string[]) =>
      post<Order>(`/api/orders/${id}/items/reorder`, { course, itemIds }),
    /** One line or a whole party's worth, in a single all-or-nothing transfer. */
    moveItems: (
      id: string,
      body: { targetTableId: string; items: { orderItemId: string; quantity?: number }[] },
    ) => post<MoveItemsResponse>(`/api/orders/${id}/items/move`, body),
    setItemDiscount: (
      id: string,
      itemId: string,
      body: { type: 'PERCENT' | 'FIXED'; value: number; reason?: string | null },
    ) => put<Order>(`/api/orders/${id}/items/${itemId}/discount`, body),
    clearItemDiscount: (id: string, itemId: string) =>
      del<Order>(`/api/orders/${id}/items/${itemId}/discount`),
    send: (id: string, body: unknown) => post<SendItemsResponse>(`/api/orders/${id}/send`, body),
    requestPayment: (id: string) => post<Order>(`/api/orders/${id}/request-payment`),
    /** `value` is basis points for PERCENT and cents for FIXED. */
    setDiscount: (
      id: string,
      body: { type: 'PERCENT' | 'FIXED'; value: number; reason?: string | null },
    ) => put<Order>(`/api/orders/${id}/discount`, body),
    clearDiscount: (id: string) => del<Order>(`/api/orders/${id}/discount`),
    pay: (id: string, body: unknown) => post<Order>(`/api/orders/${id}/payment`, body),
    voidPayment: (id: string, paymentId: string, reason: string) =>
      post<Order>(`/api/orders/${id}/payments/${paymentId}/void`, { reason }),
    /** Live total for a proposed split-by-item payment, before any money changes hands. */
    splitPaymentQuote: (id: string, selections: { orderItemId: string; quantity: number }[]) =>
      post<SplitPaymentQuote>(`/api/orders/${id}/split-payment/quote`, { selections }),
    splitPayment: (id: string, body: unknown) => post<Order>(`/api/orders/${id}/split-payment`, body),
    invoice: (id: string) => get<InvoiceSummary | null>(`/api/orders/${id}/invoice`),
    issueInvoice: (id: string) => post<InvoiceSummary>(`/api/orders/${id}/invoice`),
    setInvoiceBuyer: (id: string, body: unknown) => patch<Order>(`/api/orders/${id}/invoice-buyer`, body),
    close: (id: string, options: { force?: boolean; isInvite?: boolean } = {}) =>
      post<Order>(`/api/orders/${id}/close`, { force: false, ...options }),
    /** Queue the bill for the cashier's printer rather than printing here. `paymentId` prints just that split's slice. */
    print: (id: string, kind: PrintJobKind, copies = 1, paymentId?: string) =>
      post<PrintJob>(`/api/orders/${id}/print`, { kind, copies, paymentId }),
    printJobs: (id: string) => get<PrintJob[]>(`/api/orders/${id}/print-jobs`),
    cancel: (id: string, reason: string) => post<Order>(`/api/orders/${id}/cancel`, { reason }),
    /** Cancel an unsettled order from the history screen, clearing its tickets and freeing the table. Admin, with password. */
    remove: (id: string, password: string) => post<Order>(`/api/orders/${id}/remove`, { password }),
  },

  reports: {
    /** Every management figure for one window, in a single request. */
    sales: (params: Record<string, string | number | undefined>) =>
      get<SalesReport>(`/api/reports/sales${query(params)}`),
    /** One evening's complimentary items and discounts; `date` is a business day. */
    giveaways: (date: string) => get<GiveawayReport>(`/api/reports/giveaways/${date}`),
  },

  printJobs: {
    list: (params: { status?: PrintJobStatus; limit?: number } = {}) =>
      get<PrintJob[]>(`/api/print-jobs${query(params)}`),
    get: (id: string) => get<PrintJob>(`/api/print-jobs/${id}`),
    /** Station only: take the next jobs off the queue, documents attached. */
    claim: (stationName: string, limit?: number) =>
      post<ClaimedPrintJob[]>('/api/print-jobs/claim', { stationName, limit }),
    done: (id: string) => post<PrintJob>(`/api/print-jobs/${id}/done`),
    failed: (id: string, message?: string) =>
      post<PrintJob>(`/api/print-jobs/${id}/failed`, { message }),
    retry: (id: string) => post<PrintJob>(`/api/print-jobs/${id}/retry`),
    cancel: (id: string) => post<PrintJob>(`/api/print-jobs/${id}/cancel`),
  },

  categories: {
    list: (includeInactive = false) =>
      get<Category[]>(`/api/categories${query({ includeInactive: includeInactive ? 'true' : '' })}`),
    tree: (includeInactive = false) =>
      get<CategoryNode[]>(
        `/api/categories${query({ tree: 'true', includeInactive: includeInactive ? 'true' : '' })}`,
      ),
    create: (body: unknown) => post<Category>('/api/categories', body),
    update: (id: string, body: unknown) => patch<Category>(`/api/categories/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/api/categories/${id}`),
    reorder: (items: { id: string; sortOrder: number }[]) =>
      post<{ ok: true }>('/api/categories/reorder', { items }),
  },

  menuItems: {
    list: (params: { includeInactive?: boolean; categoryId?: string } = {}) =>
      get<MenuItem[]>(
        `/api/menu-items${query({
          includeInactive: params.includeInactive ? 'true' : '',
          categoryId: params.categoryId,
        })}`,
      ),
    get: (id: string) => get<MenuItem>(`/api/menu-items/${id}`),
    create: (body: unknown) => post<MenuItem>('/api/menu-items', body),
    update: (id: string, body: unknown) => patch<MenuItem>(`/api/menu-items/${id}`, body),
    remove: (id: string) => del<{ deactivated: boolean }>(`/api/menu-items/${id}`),
  },

  modifiers: {
    list: (includeInactive = false) =>
      get<ModifierGroup[]>(
        `/api/modifier-groups${query({ includeInactive: includeInactive ? 'true' : '' })}`,
      ),
    createGroup: (body: unknown) => post<ModifierGroup>('/api/modifier-groups', body),
    updateGroup: (id: string, body: unknown) => patch<ModifierGroup>(`/api/modifier-groups/${id}`, body),
    removeGroup: (id: string) => del<{ ok: true }>(`/api/modifier-groups/${id}`),
    createOption: (groupId: string, body: unknown) =>
      post<ModifierOption>(`/api/modifier-groups/${groupId}/options`, body),
    updateOption: (optionId: string, body: unknown) =>
      patch<ModifierOption>(`/api/modifier-groups/options/${optionId}`, body),
    removeOption: (optionId: string) => del<{ ok: true }>(`/api/modifier-groups/options/${optionId}`),
  },

  station: (destination: TicketedDestination) => {
    const base = destination === 'KITCHEN' ? '/api/kitchen' : '/api/bar';
    return {
      tickets: (completedWithinMinutes = 0) =>
        get<ProductionTicket[]>(`${base}/tickets${query({ completedWithinMinutes })}`),
      setTicketStatus: (ticketId: string, status: string) =>
        patch<ProductionTicket[]>(`${base}/tickets/${ticketId}`, { status }),
      setItemStatus: (itemId: string, status: string) =>
        patch<ProductionTicket[]>(`${base}/items/${itemId}`, { status }),
    };
  },

  settings: {
    get: () => get<RestaurantSettings>('/api/settings'),
    update: (body: unknown) => patch<RestaurantSettings>('/api/settings', body),
    invoicing: () => get<InvoicingSettings>('/api/settings/invoicing'),
    updateInvoicing: (body: unknown) => put<InvoicingSettings>('/api/settings/invoicing', body),
    testInvoicing: (body: unknown) =>
      post<{ ok: boolean; message: string }>('/api/settings/invoicing/test', body),
    bookings: () => get<BookingSettings>('/api/settings/bookings'),
    updateBookings: (body: { enabled: boolean; token?: string }) =>
      put<BookingSettings>('/api/settings/bookings', body),
    testBookings: (body: { token?: string }) =>
      post<{ ok: boolean; message: string }>('/api/settings/bookings/test', body),
  },

  reservations: {
    list: (date: string) => get<Reservation[]>(`/api/reservations${query({ date })}`),
    upcoming: () => get<Reservation[]>('/api/reservations/upcoming'),
    /** Live slot occupancy from Bistrochat. */
    availability: (date: string, partySize: number) =>
      get<BookingAvailability>(`/api/reservations/availability${query({ date, partySize })}`),
    create: (body: unknown) => post<Reservation>('/api/reservations', body),
    update: (id: string, body: unknown) => patch<Reservation>(`/api/reservations/${id}`, body),
    setStatus: (id: string, status: Exclude<ReservationStatus, 'SEATED'>) =>
      post<Reservation>(`/api/reservations/${id}/status`, { status }),
    /** Opens their table in the POS. */
    seat: (id: string, tableId?: string) => post<Reservation>(`/api/reservations/${id}/seat`, { tableId }),
  },

  users: {
    list: () => get<StaffUser[]>('/api/users'),
    create: (body: unknown) => post<StaffUser>('/api/users', body),
    update: (id: string, body: unknown) => patch<StaffUser>(`/api/users/${id}`, body),
    deactivate: (id: string) => del<StaffUser>(`/api/users/${id}`),
  },

  audit: {
    list: (params: Record<string, string | number | undefined>) =>
      get<Paginated<AuditLogEntry>>(`/api/audit-logs${query(params)}`),
  },
};
